import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ENFORCEMENT_LEVELS = ['OFF', 'REMINDER', 'PERSISTENT_WARNING', 'BLOCKING'] as const;
type EnforcementLevel = (typeof ENFORCEMENT_LEVELS)[number];

const ENFORCEMENT_ACTION_KEYS = [
  'clock_in',
  'return_from_lunch',
  'return_from_break',
  'return_from_bathroom',
  'resume_work',
  'no_schedule',
] as const;

const ACTION_LABELS: Record<(typeof ENFORCEMENT_ACTION_KEYS)[number], string> = {
  clock_in: 'Clock In',
  return_from_lunch: 'Return from Lunch',
  return_from_break: 'Return from Break',
  return_from_bathroom: 'Return from Bathroom',
  resume_work: 'Resume Work',
  no_schedule: 'No Schedule',
};

/** Permanently unrestricted — never shown in Role Assignment. */
const ENFORCEMENT_EXEMPT_ROLES = new Set(['super_user']);

function isEnforcementExemptRole(roleName: string): boolean {
  return ENFORCEMENT_EXEMPT_ROLES.has(String(roleName || '').trim().toLowerCase());
}

type PolicySettings = {
  authentication: {
    require_teks_login: boolean;
    require_reauth_after_idle_lock: boolean;
    login_enforcement: EnforcementLevel;
  };
  attendance: {
    require_clock_in: boolean;
    require_clock_out: boolean;
    track_late_arrival: boolean;
    require_lunch_break_actions: boolean;
  };
  enforcement: Record<(typeof ENFORCEMENT_ACTION_KEYS)[number], EnforcementLevel>;
  idle: {
    detection_enabled: boolean;
    threshold_minutes: number;
    windows_lock_on_idle: boolean;
    require_teks_reauth_after_unlock: boolean;
  };
  notifications: {
    windows_notification: boolean;
    persistent_teks_warning: boolean;
    escalate_after_minutes: number;
    notify_supervisor: boolean;
  };
};

type PolicyRow = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  version: number;
  updated_at: string | null;
  assigned_roles: string[];
  settings: PolicySettings;
};

type RoleAssignment = {
  role_name: string;
  policy_id: string;
  policy_code?: string | null;
  policy_name?: string | null;
};

const LevelSelect: React.FC<{
  label: string;
  value: EnforcementLevel;
  disabled?: boolean;
  onChange: (value: EnforcementLevel) => void;
}> = ({ label, value, disabled, onChange }) => (
  <FormControl fullWidth margin="dense" size="small" disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as EnforcementLevel)}
    >
      {ENFORCEMENT_LEVELS.map((level) => (
        <MenuItem key={level} value={level}>{level}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

/*
 * Optional employee Effective Enforcement Policy display was skipped: Workforce
 * Monitoring has no clean employee detail panel that already surfaces role.
 * Use getEffectiveEnforcementPolicy(employeeId) when such a panel is added.
 */
const EnforcementPoliciesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const canRead = permissions.includes('monitoring.enforcement_policies.read')
    || permissions.includes('monitoring.console.read');
  const canManage = permissions.includes('monitoring.enforcement_policies.manage');
  const canAssign = permissions.includes('monitoring.enforcement_policies.assign');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PolicyRow | null>(null);
  const [saved, setSaved] = useState(false);
  const [roleMap, setRoleMap] = useState<Record<string, string>>({});
  const [assignSaved, setAssignSaved] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['enforcementPolicies'],
    queryFn: () => apiClient.monitoring.listEnforcementPolicies(),
    enabled: canRead,
  });

  const { data: rolesResponse } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.roles.getAll(),
    enabled: canRead,
  });

  const policies: PolicyRow[] = useMemo(
    () => data?.data?.data?.policies || [],
    [data],
  );
  const roleAssignments: RoleAssignment[] = useMemo(
    () => data?.data?.data?.role_assignments || [],
    [data],
  );

  const discoveredRoles = useMemo(() => {
    const rows = rolesResponse?.data?.data || [];
    return rows
      .filter((role: { name?: string; isActive?: boolean }) => role.isActive !== false)
      .map((role: { name?: string }) => String(role.name || '').trim().toLowerCase())
      .filter((name: string) => name && !isEnforcementExemptRole(name))
      .sort();
  }, [rolesResponse]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const role of discoveredRoles) next[role] = '';
    for (const row of roleAssignments) {
      if (isEnforcementExemptRole(row.role_name)) continue;
      next[row.role_name] = row.policy_id;
    }
    setRoleMap(next);
  }, [roleAssignments, discoveredRoles]);

  useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      return;
    }
    const found = policies.find((p) => p.id === selectedId);
    if (found) {
      setDraft(JSON.parse(JSON.stringify(found)));
      setSaved(false);
    }
  }, [selectedId, policies]);

  const rolesForUi = useMemo(() => {
    const names = new Set<string>(discoveredRoles);
    for (const role of Object.keys(roleMap)) {
      if (!isEnforcementExemptRole(role)) names.add(role);
    }
    return Array.from(names).sort();
  }, [discoveredRoles, roleMap]);

  const saveMutation = useMutation({
    mutationFn: (payload: { id: string; body: Record<string, unknown> }) =>
      apiClient.monitoring.updateEnforcementPolicy(payload.id, payload.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enforcementPolicies'] });
      setSaved(true);
    },
  });

  const assignMutation = useMutation({
    mutationFn: (payload: { roleName: string; policyId: string }) =>
      apiClient.monitoring.assignEnforcementPolicyToRole(payload.roleName, payload.policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enforcementPolicies'] });
      setAssignSaved(true);
    },
  });

  const patchSettings = (path: string, value: unknown) => {
    setSaved(false);
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, settings: { ...prev.settings } };
      const parts = path.split('.');
      if (parts[0] === 'authentication') {
        next.settings.authentication = { ...next.settings.authentication, [parts[1]]: value } as PolicySettings['authentication'];
      } else if (parts[0] === 'attendance') {
        next.settings.attendance = { ...next.settings.attendance, [parts[1]]: value } as PolicySettings['attendance'];
      } else if (parts[0] === 'enforcement') {
        next.settings.enforcement = { ...next.settings.enforcement, [parts[1]]: value } as PolicySettings['enforcement'];
      } else if (parts[0] === 'idle') {
        next.settings.idle = { ...next.settings.idle, [parts[1]]: value } as PolicySettings['idle'];
      } else if (parts[0] === 'notifications') {
        next.settings.notifications = { ...next.settings.notifications, [parts[1]]: value } as PolicySettings['notifications'];
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!draft || !canManage) return;
    setSaved(false);
    saveMutation.mutate({
      id: draft.id,
      body: {
        name: draft.name,
        description: draft.description,
        enabled: draft.enabled,
        settings: draft.settings,
      },
    });
  };

  const handleAssignRole = (roleName: string, policyId: string) => {
    if (!canAssign || !policyId) return;
    setAssignSaved(false);
    setRoleMap((prev) => ({ ...prev, [roleName]: policyId }));
    assignMutation.mutate({ roleName, policyId });
  };

  if (!canRead) {
    return (
      <Box sx={{ mt: 1 }}>
        <Alert severity="warning">
          Enforcement policies require monitoring.enforcement_policies.read or monitoring.console.read.
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load enforcement policies.</Alert>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/workforce-monitoring/configuration')}
        sx={{ mb: 2 }}
      >
        Back to Configuration
      </Button>

      <Typography variant="h5" gutterBottom>Enforcement Policies</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Windows agent enforcement intensity by role. Attendance truth is unchanged; these settings control
        reminders, persistent warnings, and desktop blockers.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Assigned Roles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No policies found.</TableCell>
              </TableRow>
            ) : policies.map((policy) => (
              <TableRow
                key={policy.id}
                hover
                selected={selectedId === policy.id}
                onClick={() => setSelectedId(policy.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2">{policy.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{policy.code}</Typography>
                </TableCell>
                <TableCell>
                  {(policy.assigned_roles || []).filter((role) => !isEnforcementExemptRole(role)).length === 0
                    ? '—'
                    : (policy.assigned_roles || [])
                      .filter((role) => !isEnforcementExemptRole(role))
                      .map((role) => (
                      <Chip key={role} size="small" label={role} sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={policy.enabled ? 'success' : 'default'}
                    label={policy.enabled ? 'Enabled' : 'Disabled'}
                  />
                </TableCell>
                <TableCell>v{policy.version}</TableCell>
                <TableCell>
                  {policy.updated_at ? moment(policy.updated_at).format('MMM D, YYYY h:mm A') : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {draft && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Edit: {draft.name}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  label="Name"
                  value={draft.name}
                  disabled={!canManage}
                  onChange={(e) => {
                    setSaved(false);
                    setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev));
                  }}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label="Description"
                  value={draft.description || ''}
                  disabled={!canManage}
                  onChange={(e) => {
                    setSaved(false);
                    setDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev));
                  }}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.enabled)}
                      disabled={!canManage}
                      onChange={(e) => {
                        setSaved(false);
                        setDraft((prev) => (prev ? { ...prev, enabled: e.target.checked } : prev));
                      }}
                    />
                  )}
                  label="Enabled"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>AUTHENTICATION</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.authentication.require_teks_login)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('authentication.require_teks_login', e.target.checked)}
                    />
                  )}
                  label="Require TEKS login"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.authentication.require_reauth_after_idle_lock)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('authentication.require_reauth_after_idle_lock', e.target.checked)}
                    />
                  )}
                  label="Reauth after idle lock"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LevelSelect
                  label="Login enforcement"
                  value={draft.settings.authentication.login_enforcement}
                  disabled={!canManage}
                  onChange={(v) => patchSettings('authentication.login_enforcement', v)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>ATTENDANCE</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.attendance.require_clock_in)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('attendance.require_clock_in', e.target.checked)}
                    />
                  )}
                  label="Require clock in"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.attendance.require_clock_out)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('attendance.require_clock_out', e.target.checked)}
                    />
                  )}
                  label="Require clock out"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.attendance.track_late_arrival)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('attendance.track_late_arrival', e.target.checked)}
                    />
                  )}
                  label="Track late arrival"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.attendance.require_lunch_break_actions)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('attendance.require_lunch_break_actions', e.target.checked)}
                    />
                  )}
                  label="Require lunch/break actions"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>ENFORCEMENT</Typography>
            <Grid container spacing={1}>
              {ENFORCEMENT_ACTION_KEYS.map((key) => (
                <Grid item xs={12} md={4} key={key}>
                  <LevelSelect
                    label={ACTION_LABELS[key]}
                    value={draft.settings.enforcement[key]}
                    disabled={!canManage}
                    onChange={(v) => patchSettings(`enforcement.${key}`, v)}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>IDLE</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.idle.detection_enabled)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('idle.detection_enabled', e.target.checked)}
                    />
                  )}
                  label="Detection enabled"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.idle.windows_lock_on_idle)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('idle.windows_lock_on_idle', e.target.checked)}
                    />
                  )}
                  label="Windows lock on idle"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.idle.require_teks_reauth_after_unlock)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('idle.require_teks_reauth_after_unlock', e.target.checked)}
                    />
                  )}
                  label="TEKS reauth after unlock"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  type="number"
                  label="Threshold (minutes)"
                  value={draft.settings.idle.threshold_minutes}
                  disabled={!canManage}
                  onChange={(e) => patchSettings('idle.threshold_minutes', Number(e.target.value))}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>NOTIFICATIONS</Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.notifications.windows_notification)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('notifications.windows_notification', e.target.checked)}
                    />
                  )}
                  label="Windows notification"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.notifications.persistent_teks_warning)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('notifications.persistent_teks_warning', e.target.checked)}
                    />
                  )}
                  label="Persistent TEKS warning"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(draft.settings.notifications.notify_supervisor)}
                      disabled={!canManage}
                      onChange={(e) => patchSettings('notifications.notify_supervisor', e.target.checked)}
                    />
                  )}
                  label="Notify supervisor"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  type="number"
                  label="Escalate after (minutes)"
                  value={draft.settings.notifications.escalate_after_minutes}
                  disabled={!canManage}
                  onChange={(e) => patchSettings('notifications.escalate_after_minutes', Number(e.target.value))}
                />
              </Grid>
            </Grid>

            {!canManage && (
              <Alert severity="info" sx={{ mt: 2 }}>
                View only — editing requires monitoring.enforcement_policies.manage.
              </Alert>
            )}
            {canManage && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                >
                  Save Policy
                </Button>
                {saved && <Alert severity="success">Policy saved.</Alert>}
                {saveMutation.isError && (
                  <Alert severity="error">Failed to save policy.</Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Role Assignment</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Map each role to an enforcement policy. Roles come from Roles Management automatically —
            newly added active roles appear here and default to Strict Agent until you change them.
            The super_user role is permanently unrestricted and is not listed here.
          </Typography>
          <Grid container spacing={2}>
            {rolesForUi.map((roleName) => (
              <Grid item xs={12} md={6} key={roleName}>
                <FormControl fullWidth size="small" disabled={!canAssign}>
                  <InputLabel>{roleName}</InputLabel>
                  <Select
                    label={roleName}
                    value={roleMap[roleName] || ''}
                    onChange={(e) => handleAssignRole(roleName, String(e.target.value))}
                  >
                    <MenuItem value="">
                      <em>Select policy</em>
                    </MenuItem>
                    {policies.filter((p) => p.enabled).map((policy) => (
                      <MenuItem key={policy.id} value={policy.id}>
                        {policy.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
          {!canAssign && (
            <Alert severity="info" sx={{ mt: 2 }}>
              View only — role assignment requires monitoring.enforcement_policies.assign.
            </Alert>
          )}
          {assignSaved && <Alert severity="success" sx={{ mt: 2 }}>Role assignment saved.</Alert>}
          {assignMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>Failed to assign policy to role.</Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnforcementPoliciesPage;
